import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import {
  RotateCcw,
  Mail,
  CheckCircle2,
  AlertTriangle,
  AlertOctagon,
  Info,
  FileText,
  ArrowRight,
  Lock,
  Sparkles,
} from "lucide-react";
import ScoreGauge from "@/components/ScoreGauge";
import MultaResultCard from "@/components/MultaResultCard";
import DeepScanTeaser from "@/components/DeepScanTeaser";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RISK_STYLES = {
  alto: {
    badge: "bg-rose-100 text-rose-800 border-rose-200",
    icon: AlertOctagon,
    label: "Riesgo alto",
  },
  medio: {
    badge: "bg-amber-100 text-amber-800 border-amber-200",
    icon: AlertTriangle,
    label: "Riesgo medio",
  },
  bajo: {
    badge: "bg-emerald-100 text-emerald-800 border-emerald-200",
    icon: CheckCircle2,
    label: "Riesgo bajo",
  },
};

function scoreColor(score) {
  if (score >= 80) return { hex: "#166534", label: "Buen estado", tone: "emerald" };
  if (score >= 50) return { hex: "#92400E", label: "Necesita atención", tone: "amber" };
  return { hex: "#991B1B", label: "Riesgo elevado", tone: "rose" };
}

// Mapeo heurístico: cada hallazgo se conecta al documento Pro que lo resuelve.
// Match por palabras clave en el título o recomendación.
const DOC_MAP = [
  {
    keywords: ["politica de privacidad", "política de privacidad", "privacidad", "responsable", "contacto del responsable", "arco", "derechos del titular", "cookies", "transferencia"],
    docId: "politica_privacidad",
    docName: "Política de Privacidad",
    ctaText: "Generar Política de Privacidad",
  },
  {
    keywords: ["consentimiento", "formulario", "formularios", "checkbox", "opt-in", "marketing", "newsletter"],
    docId: "clausula_consentimiento",
    docName: "Cláusula de Consentimiento",
    ctaText: "Generar Cláusula de Consentimiento",
  },
  {
    keywords: ["scripts de terceros", "encargado", "proveedor", "tercero", "dpa", "google analytics", "facebook pixel", "mailchimp", "tag manager"],
    docId: "dpa",
    docName: "Contrato con Encargado de Datos (DPA)",
    ctaText: "Generar DPA con tu proveedor",
  },
  {
    keywords: ["registro de actividades", "rat", "fiscalización", "fiscalizacion", "apdp", "medidas de seguridad", "brecha"],
    docId: "rat",
    docName: "Catálogo de Datos Personales (RAT)",
    ctaText: "Generar RAT (obligatorio art. 16)",
  },
];

function findRelevantDoc(h) {
  const text = `${h.titulo || ""} ${h.recomendacion || ""} ${h.que_encontramos || ""}`.toLowerCase();
  for (const m of DOC_MAP) {
    if (m.keywords.some((kw) => text.includes(kw))) return m;
  }
  return null;
}

export default function Report({ result, onReset }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Usuarios autenticados (Pro, Free registrado o Superadmin) tienen el reporte
  // desbloqueado automáticamente — no necesitan pasar por el email wall.
  const isAuthenticated = !!user?.email;
  const [unlocked, setUnlocked] = useState(isAuthenticated);
  const [emailUsed, setEmailUsed] = useState(isAuthenticated ? user.email : "");
  const [publicSlug, setPublicSlug] = useState(null);

  const sc = scoreColor(result.score);

  const counts = result.hallazgos.reduce(
    (acc, h) => {
      acc[h.riesgo] = (acc[h.riesgo] || 0) + 1;
      return acc;
    },
    { alto: 0, medio: 0, bajo: 0 }
  );

  const onUnlocked = (email, slug) => {
    setEmailUsed(email);
    setPublicSlug(slug);
    setUnlocked(true);
  };

  // Si el usuario se autentica después de cargar el reporte, desbloqueamos
  useEffect(() => {
    if (isAuthenticated && !unlocked) {
      setUnlocked(true);
      setEmailUsed(user.email);
    }
  }, [isAuthenticated, unlocked, user?.email]);

  return (
    <section className="px-6 py-12 sm:py-16">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <p className="text-xs font-semibold tracking-widest uppercase text-slate-500">
              Informe de cumplimiento
            </p>
            <h1
              data-testid="report-url"
              className="mt-2 font-display text-2xl sm:text-3xl font-bold text-slate-900 break-all"
            >
              {result.url}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {new Date(result.creado_en).toLocaleString("es-CL", {
                dateStyle: "long",
                timeStyle: "short",
              })}
            </p>
          </div>
          <button
            data-testid="rescan-btn"
            onClick={onReset}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-sm font-medium text-slate-700 transition-colors w-fit"
          >
            <RotateCcw className="w-4 h-4" />
            Analizar otro sitio
          </button>
        </div>

        {/* Score + Resumen */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-5 bg-white border border-slate-200 rounded-2xl p-8 shadow-sm flex flex-col items-center text-center"
          >
            <ScoreGauge score={result.score} color={sc.hex} />
            <p className="mt-6 text-xs font-semibold tracking-widest uppercase text-slate-500">
              Score de cumplimiento
            </p>
            <p
              data-testid="score-label"
              className="mt-2 font-display text-lg font-semibold"
              style={{ color: sc.hex }}
            >
              {sc.label}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-7 bg-slate-50 border border-slate-200 rounded-2xl p-8"
          >
            <div className="flex items-start gap-3 mb-4">
              <Info className="w-5 h-5 text-[#1E3A8A] mt-0.5 shrink-0" />
              <h2 className="font-display text-xl font-semibold text-slate-900">
                Resumen ejecutivo
              </h2>
            </div>
            <p
              data-testid="report-summary"
              className="text-slate-700 leading-relaxed text-base"
            >
              {result.resumen}
            </p>

            <div className="mt-6 grid grid-cols-3 gap-3">
              <CountTile label="Altos" count={counts.alto || 0} tone="rose" />
              <CountTile label="Medios" count={counts.medio || 0} tone="amber" />
              <CountTile label="Bajos" count={counts.bajo || 0} tone="emerald" />
            </div>
          </motion.div>
        </div>

        {/* Multa estimada — teaser si bloqueado, full + toggle PYME si desbloqueado */}
        {result.multa_estimada && (
          <div className="mb-10" data-testid="multa-block">
            {unlocked ? (
              <MultaResultCard
                pyme={result.multa_estimada.monto_si_pyme_primera_vez}
                noPyme={result.multa_estimada.monto_si_no_pyme_o_reincidente}
                withPymeToggle
              />
            ) : (
              <MultaTeaser multa={result.multa_estimada} />
            )}
          </div>
        )}

        {/* Plan de acción — solo después de desbloquear */}
        {unlocked && (() => {
          const docsSugeridos = [];
          const seenIds = new Set();
          result.hallazgos.forEach((h) => {
            const m = findRelevantDoc(h);
            if (m && !seenIds.has(m.docId)) {
              seenIds.add(m.docId);
              docsSugeridos.push(m);
            }
          });
          if (docsSugeridos.length === 0) return null;
          return (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              data-testid="action-plan"
              className="mb-8 bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white rounded-2xl p-7 sm:p-8 shadow-lg"
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-white/15 grid place-items-center shrink-0">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold tracking-wider uppercase text-blue-200">Plan de acción para subir tu score</p>
                  <h2 className="font-display text-xl sm:text-2xl font-bold mt-1">
                    Con {docsSugeridos.length} {docsSugeridos.length === 1 ? "documento" : "documentos"} resuelves la mayoría de los hallazgos
                  </h2>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-5">
                {docsSugeridos.map((d, i) => (
                  <Link
                    key={d.docId}
                    to="/modulo-pro"
                    data-testid={`action-plan-cta-${d.docId}`}
                    className="group flex items-center gap-3 bg-white/10 hover:bg-white/15 border border-white/15 rounded-xl p-3.5 transition-all"
                  >
                    <div className="w-8 h-8 rounded-full bg-white text-[#1E3A8A] grid place-items-center text-xs font-bold shrink-0">{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-white">{d.docName}</div>
                      <div className="text-xs text-blue-200">Generar con Módulo Pro</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-blue-200 group-hover:translate-x-0.5 group-hover:text-white transition-all shrink-0" />
                  </Link>
                ))}
              </div>
              <p className="mt-5 text-xs text-blue-200">
                Todo incluido en el Módulo Pro: <strong className="text-white">$14.990 CLP · pago único · acceso permanente</strong>.
              </p>
            </motion.div>
          );
        })()}

        {/* Hallazgos */}
        <div className="mb-8">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 mb-2">
            {unlocked ? `Hallazgos (${result.hallazgos.length})` : "Puntos detectados — vista previa"}
          </h2>
          {!unlocked && (
            <p className="text-sm text-slate-500 mb-6">Estos son los puntos que tu sitio tiene buenos y malos. Para ver qué exige la ley en cada uno y cómo resolverlos, ingresa tu email más abajo.</p>
          )}

          <div className={unlocked ? "space-y-4" : "space-y-3"} data-testid="findings-list">
            {result.hallazgos.map((h, i) => (
              <FindingCard key={i} h={h} index={i} unlocked={unlocked} />
            ))}
          </div>
        </div>

        {/* Upsell: Deep Scan Pro teaser — solo aparece si el backend lo incluye (free users) */}
        <DeepScanTeaser teaser={result.deep_scan_teaser} />

        {/* Muro de email O confirmación de desbloqueo */}
        <AnimatePresence mode="wait">
          {!unlocked ? (
            <EmailWall
              key="wall"
              scanId={result.id}
              onUnlocked={onUnlocked}
              riesgosAltos={counts.alto}
            />
          ) : (
            <UnlockedConfirm key="ok" email={emailUsed} />
          )}
        </AnimatePresence>

        {/* CTA Módulo Pro — solo después de desbloquear */}
        {unlocked && (
          <div
            data-testid="generator-cta"
            className="mt-6 bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white rounded-2xl p-8 sm:p-10 relative overflow-hidden"
          >
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white/5 blur-3xl" />
            <div className="relative max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold uppercase tracking-wide">
                <FileText className="w-3.5 h-3.5" />
                Resuelve cada hallazgo
              </div>
              <h3 className="mt-4 font-display text-2xl sm:text-3xl font-bold tracking-tight">
                Resuelve estos puntos con el Módulo Pro
              </h3>
              <p className="mt-2 text-white/80">
                $14.990 CLP · pago único · acceso permanente. Documentos legales conformes a la Ley 21.719 + guías paso a paso por plataforma + alertas cuando cambia la ley.
              </p>
              <button
                data-testid="goto-generator-btn"
                onClick={() => navigate("/modulo-pro")}
                className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-white text-[#1E3A8A] font-semibold hover:bg-slate-100 active:scale-[0.98] transition-all"
              >
                Ver el Módulo Pro
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function EmailWall({ scanId, onUnlocked, riesgosAltos }) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(true); // default ON, usuario puede desmarcar
  const [publicar, setPublicar] = useState(false);
  const [nombrePublico, setNombrePublico] = useState("");
  const [sending, setSending] = useState(false);

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canSubmit = emailOk && consent && !sending;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/scan/unlock`, {
        scan_id: scanId,
        email,
        consentimiento: consent,
        consentimiento_marketing: marketingConsent,
        publicar,
        nombre_publico: publicar ? (nombrePublico || null) : null,
      });
      onUnlocked(email, data?.public_slug || null);
      trackEvent("lead_captured", {
        publicar: !!publicar,
        marketing_opt_in: !!marketingConsent,
        riesgos_alto: riesgosAltos || 0,
      });
      const msg = marketingConsent
        ? "Recomendaciones desbloqueadas. Te enviamos el informe + 3 emails educativos sobre la ley."
        : "Recomendaciones desbloqueadas. Te enviamos el informe a tu correo.";
      toast.success(msg);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos desbloquear el informe. Reintenta.");
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -18 }}
      transition={{ duration: 0.5 }}
      data-testid="email-wall"
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-[#0F172A] to-[#1E3A8A] text-white shadow-2xl"
    >
      {/* Top fake-blurred preview row */}
      <div className="absolute inset-x-0 top-0 px-8 sm:px-12 pt-10 pointer-events-none select-none">
        <div className="space-y-3">
          <div className="h-3.5 bg-white/15 rounded blur-sm w-3/4" />
          <div className="h-3.5 bg-white/15 rounded blur-sm w-5/6" />
          <div className="h-3.5 bg-white/15 rounded blur-sm w-2/3" />
        </div>
      </div>

      <div className="relative px-8 sm:px-12 pt-32 pb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold uppercase tracking-wide">
          <Lock className="w-3.5 h-3.5" />
          Recomendaciones bloqueadas
        </div>
        <h3 className="mt-5 font-display text-3xl sm:text-4xl font-bold tracking-tight">
          Deja tu email y desbloquea cómo resolver cada punto
        </h3>
        <p className="mt-3 text-white/80 max-w-xl text-base leading-relaxed">
          {riesgosAltos > 0
            ? `Detectamos ${riesgosAltos} hallazgo${riesgosAltos > 1 ? "s" : ""} de riesgo alto. Te mostramos qué hacer concretamente para resolver cada uno`
            : "Te mostramos las acciones concretas para resolver cada hallazgo"}
          , y además te enviamos una copia del informe completo a tu correo.
        </p>

        <form onSubmit={submit} className="mt-8 max-w-lg space-y-4">
          <input
            data-testid="wall-email-input"
            type="email"
            required
            placeholder="tu@empresa.cl"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full h-12 px-4 bg-white text-slate-900 placeholder:text-slate-400 border-2 border-transparent rounded-lg focus:border-white focus:outline-none"
          />

          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              data-testid="wall-consent-checkbox"
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 w-5 h-5 rounded border-white/40 bg-white/10 text-[#1E40AF] focus:ring-2 focus:ring-white shrink-0 cursor-pointer accent-white"
            />
            <span className="text-sm text-white/80 leading-relaxed">
              Acepto recibir mi informe y comunicaciones de Ley de Datos Chile sobre la Ley 21.719. Puedo darme de baja cuando quiera. Ver{" "}
              <Link
                to="/politica-privacidad"
                target="_blank"
                rel="noopener"
                className="underline hover:text-white"
              >
                política de privacidad
              </Link>
              .
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              data-testid="wall-marketing-checkbox"
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
              className="mt-0.5 w-5 h-5 rounded border-white/40 bg-white/10 text-[#1E40AF] focus:ring-2 focus:ring-white shrink-0 cursor-pointer accent-white"
            />
            <span className="text-sm text-white/80 leading-relaxed">
              También recibir 3 emails educativos sobre la ley (en 24h, 3 días y 1 semana). Cada uno con link de baja al pie. <em className="text-white/60">Opcional · recomendado</em>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              data-testid="wall-publish-checkbox"
              type="checkbox"
              checked={publicar}
              onChange={(e) => setPublicar(e.target.checked)}
              className="mt-0.5 w-5 h-5 rounded border-white/40 bg-white/10 text-[#1E40AF] focus:ring-2 focus:ring-white shrink-0 cursor-pointer accent-white"
            />
            <span className="text-sm text-white/80 leading-relaxed">
              <strong className="text-white">Publicar mi score como sello de transparencia.</strong> Las empresas que muestran su cumplimiento Ley 21.719 generan confianza en sus clientes. Solo se publica tu score y 1-2 hallazgos destacados — nunca el detalle técnico completo.
            </span>
          </label>

          {publicar && (
            <input
              data-testid="wall-publish-name"
              type="text"
              placeholder="Nombre visible (ej: Mi Tienda, Clínica X)"
              value={nombrePublico}
              onChange={(e) => setNombrePublico(e.target.value)}
              maxLength={60}
              className="w-full h-11 px-4 bg-white/10 text-white placeholder:text-white/50 border-2 border-white/20 rounded-lg focus:border-white focus:outline-none"
            />
          )}

          <button
            data-testid="wall-submit-btn"
            type="submit"
            disabled={!canSubmit}
            className="h-12 px-7 rounded-lg bg-white text-[#1E3A8A] font-semibold hover:bg-slate-100 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all inline-flex items-center justify-center gap-2"
          >
            {sending ? "Desbloqueando…" : "Ver mi informe completo"}
            {!sending && <ArrowRight className="w-4 h-4" />}
          </button>

          <p className="text-xs text-white/50">
            Tu email no se vende ni se comparte con terceros. Esto es Ley de Datos Chile practicando lo que predica.
          </p>
        </form>
      </div>
    </motion.div>
  );
}

function UnlockedConfirm({ email, publicSlug }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
      data-testid="unlocked-confirm"
      className="rounded-2xl bg-emerald-50 border-2 border-emerald-200 p-6 sm:p-8"
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-emerald-500 grid place-items-center shrink-0">
          <CheckCircle2 className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-lg sm:text-xl font-semibold text-emerald-900">
            Recomendaciones desbloqueadas
          </h3>
          <p className="mt-1 text-sm text-emerald-800">
            Te enviamos también una copia del informe completo a <strong>{email}</strong>. Si no llega en unos minutos, revisa la carpeta de spam.
          </p>
          {publicSlug && (
            <div data-testid="public-score-link" className="mt-4 rounded-lg bg-white border border-emerald-200 p-3 text-sm">
              <p className="text-emerald-900 font-semibold">Tu página pública está lista</p>
              <p className="mt-1 text-emerald-800">
                Comparte <Link to={`/score/${publicSlug}`} className="underline font-semibold text-[#1E3A8A]">leydedatoschile.com/score/{publicSlug}</Link> en LinkedIn o WhatsApp.
              </p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function CountTile({ label, count, tone }) {
  const tones = {
    rose: "bg-rose-50 border-rose-200 text-rose-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  };
  return (
    <div
      data-testid={`count-${tone}`}
      className={`rounded-xl border p-4 ${tones[tone]}`}
    >
      <div className="font-display text-2xl font-bold">{count}</div>
      <div className="text-xs uppercase tracking-wider font-semibold mt-1">{label}</div>
    </div>
  );
}

function FindingCard({ h, index, unlocked }) {
  const riesgo = (h.riesgo || "bajo").toLowerCase();
  const style = RISK_STYLES[riesgo] || RISK_STYLES.bajo;
  const Icon = style.icon;

  // Pre-email: vista compacta (solo badge + título). Post-email: full detalle.
  if (!unlocked) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: index * 0.05 }}
        data-testid={`finding-compact-${index}`}
        className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 flex items-center gap-4"
      >
        <span
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide border whitespace-nowrap shrink-0 ${style.badge}`}
          data-testid={`finding-risk-${index}`}
        >
          <Icon className="w-3.5 h-3.5" />
          {style.label}
        </span>
        <h3 className="font-display text-base sm:text-lg font-semibold text-slate-900 flex-1 min-w-0">
          {h.titulo}
        </h3>
        <Lock className="w-4 h-4 text-slate-300 shrink-0 hidden sm:block" aria-label="Detalle bloqueado" />
      </motion.div>
    );
  }

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: index * 0.07 }}
      data-testid={`finding-card-${index}`}
      className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1">
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide border ${style.badge}`}
            data-testid={`finding-risk-${index}`}
          >
            <Icon className="w-3.5 h-3.5" />
            {style.label}
          </span>
          <h3 className="mt-3 font-display text-lg sm:text-xl font-semibold text-slate-900">{h.titulo}</h3>
        </div>
        <div className="hidden sm:block text-3xl font-display font-bold text-slate-200 leading-none">
          {String(index + 1).padStart(2, "0")}
        </div>
      </div>

      <div className="space-y-3 mt-4">
        <Block label="Obligación legal" text={h.obligacion_legal} />
        <Block label="Qué encontramos" text={h.que_encontramos} />
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: index * 0.05 }}
          data-testid={`finding-recommendation-${index}`}
        >
          <Block label="Recomendación" text={h.recomendacion} highlight />
        </motion.div>

        {/* CTA sutil al doc Pro que resuelve este hallazgo */}
        {(() => {
          const match = findRelevantDoc(h);
          if (!match) return null;
          return (
            <Link
              to="/modulo-pro"
              data-testid={`finding-cta-pro-${index}`}
              className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-[#1E3A8A] hover:text-[#1E40AF] group"
            >
              <Sparkles className="w-3.5 h-3.5 group-hover:rotate-12 transition-transform" />
              <span className="underline-offset-4 group-hover:underline">Resolver con el Módulo Pro</span>
              <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          );
        })()}
      </div>
    </motion.article>
  );
}

function Block({ label, text, highlight }) {
  return (
    <div>
      <div className="text-[11px] font-bold tracking-widest uppercase text-slate-500 mb-1">
        {label}
      </div>
      <p className={`text-sm sm:text-base leading-relaxed ${highlight ? "text-slate-900 font-medium" : "text-slate-600"}`}>
        {text}
      </p>
    </div>
  );
}

function MultaTeaser({ multa }) {
  // Teaser borroso: mostrar la categoría, ocultar montos exactos hasta el unlock.
  const pyme = multa.monto_si_pyme_primera_vez;
  const noPyme = multa.monto_si_no_pyme_o_reincidente;
  const catLabel = multa.categoria_label || "Leve";
  const previewText = pyme?.escenario === "amonestacion_pyme_primera_vez"
    ? "AMONESTACIÓN (si calificas como PYME)"
    : `${noPyme?.monto_min_fmt || "$XXX.XXX"} – ${noPyme?.monto_max_fmt || "$X.XXX.XXX"} CLP`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="multa-teaser"
      className="rounded-2xl border-2 border-dashed border-amber-300 bg-amber-50 p-6 sm:p-7"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-amber-500 grid place-items-center shrink-0">
          <AlertOctagon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <p className="text-xs font-bold uppercase tracking-wider text-amber-700">Exposición estimada · Ley 21.719</p>
          <h3 className="font-display text-xl sm:text-2xl font-bold text-amber-950 mt-1">
            Categoría detectada: <span className="text-amber-700">{catLabel}</span>
          </h3>
        </div>
      </div>
      <div className="rounded-xl bg-white/70 border border-amber-200 p-4 mt-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-amber-600 mb-1">Tu rango aproximado</p>
        <p
          data-testid="multa-teaser-blurred"
          className="font-display text-2xl sm:text-3xl font-bold text-amber-950 select-none"
          style={{ filter: "blur(8px)", textShadow: "0 0 10px rgba(0,0,0,0.2)" }}
        >
          {previewText}
        </p>
        <p className="mt-3 text-sm text-amber-800">
          <Lock className="w-3.5 h-3.5 inline mr-1" />
          Desbloquéalo dejando tu correo más abajo. <strong>Toggle PYME instantáneo</strong> para ver tu escenario real.
        </p>
      </div>
    </motion.div>
  );
}

